import { useQuery, useMutation } from '@apollo/client/react/index.js';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  cn,
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassInput,
  GlassSelect,
} from '@merritrac/shared/components';
import {
  ArrowLeft,
  ChevronDown,
  Edit2,
  GripVertical,
  Pencil,
  Plus,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import {
  GET_CHECKLIST_TEMPLATE,
  GET_CHECKLIST_TEMPLATES,
  CREATE_CHECKLIST_TEMPLATE,
  UPDATE_CHECKLIST_TEMPLATE,
  CREATE_CHECKLIST_TEMPLATE_FIELD,
  UPDATE_CHECKLIST_TEMPLATE_FIELD,
  DELETE_CHECKLIST_TEMPLATE_FIELD,
} from '../lib/graphql/operations.js';

import type { DragEndEvent } from '@dnd-kit/core';
import type { ReactElement } from 'react';

/* ── GraphQL response types ── */

interface TemplateFieldNode {
  id: number;
  templateId: number;
  seq: number;
  fieldName: string;
  fieldType: string;
  required: number | null;
  group: string | null;
  options: string | null;
  showWhen: string | null;
}

interface TemplateData {
  checklistTemplate: {
    id: number;
    name: string;
    description: string | null;
    jobType: string | null;
    version: number | null;
    active: number | null;
    fields: TemplateFieldNode[];
  } | null;
}

interface CreateChecklistTemplateData {
  createChecklistTemplate: {
    id: number;
    name: string;
    jobType: string | null;
  } | null;
}

interface CreateChecklistTemplateFieldData {
  createChecklistTemplateField: {
    id: number;
    seq: number;
    fieldName: string;
    fieldType: string;
  } | null;
}

/* ── Types ── */

type FieldType =
  | 'text'
  | 'number'
  | 'checkbox'
  | 'picker'
  | 'photo'
  | 'signature'
  | 'date'
  | 'textarea';

interface LocalField {
  id: string;
  /** Numeric id from the API; undefined for unsaved new fields */
  apiId: number | undefined;
  displayName: string;
  fieldType: FieldType;
  required: boolean;
  group: string;
  options: string[];
  seq: number;
  showWhen: string;
}

interface LocalTemplate {
  id: number | undefined;
  name: string;
  description: string;
  jobType: string;
  fields: LocalField[];
}

/* ── Constants ── */

const FIELD_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'picker', label: 'Picker (dropdown)' },
  { value: 'photo', label: 'Photo' },
  { value: 'signature', label: 'Signature' },
  { value: 'date', label: 'Date' },
  { value: 'textarea', label: 'Text Area' },
];

const JOB_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Select job type…' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'turnover', label: 'Turnover' },
  { value: 'pest_control', label: 'Pest Control' },
];

const FIELD_TYPE_BADGE: Record<
  FieldType,
  { label: string; variant: 'default' | 'success' | 'warning' | 'danger' | 'info' }
> = {
  text: { label: 'Text', variant: 'info' },
  number: { label: 'Number', variant: 'info' },
  checkbox: { label: 'Checkbox', variant: 'success' },
  picker: { label: 'Picker', variant: 'warning' },
  photo: { label: 'Photo', variant: 'default' },
  signature: { label: 'Signature', variant: 'default' },
  date: { label: 'Date', variant: 'default' },
  textarea: { label: 'Text Area', variant: 'info' },
};

const API_FIELD_TYPE_MAP: Record<string, FieldType> = {
  text: 'text',
  input: 'text',
  number: 'number',
  checkbox: 'checkbox',
  boolean: 'checkbox',
  picker: 'picker',
  dropdown: 'picker',
  photo: 'photo',
  image: 'photo',
  signature: 'signature',
  date: 'date',
  textarea: 'textarea',
  notes: 'textarea',
};

function normalizeFieldType(raw: string | null | undefined): FieldType {
  if (raw === null || raw === undefined || raw === '') {
    return 'text';
  }
  return API_FIELD_TYPE_MAP[raw.toLowerCase()] ?? 'text';
}

/* ── Helpers ── */

function generateId(): string {
  return `f-${String(Date.now())}-${String(Math.floor(Math.random() * 1000))}`;
}

function isValidFieldType(value: string): value is FieldType {
  return [
    'text',
    'number',
    'checkbox',
    'picker',
    'photo',
    'signature',
    'date',
    'textarea',
  ].includes(value);
}

const EMPTY_TEMPLATE: LocalTemplate = {
  id: undefined,
  name: '',
  description: '',
  jobType: '',
  fields: [],
};

/* ── Preview field renderer ── */

interface PreviewFieldProps {
  field: LocalField;
}

function PreviewField({ field }: PreviewFieldProps): ReactElement {
  const baseInput = cn(
    'w-full h-9 px-3 rounded-md border border-border bg-bg text-sm text-text-secondary',
    'cursor-not-allowed opacity-60',
  );

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        <span className="text-sm font-medium text-text">{field.displayName}</span>
        {field.required && <span className="text-xs text-danger font-medium">*</span>}
      </div>

      {field.fieldType === 'text' && (
        <div className={baseInput} aria-label={`${field.displayName} text input`} />
      )}

      {field.fieldType === 'number' && (
        <div className={baseInput} aria-label={`${field.displayName} number input`} />
      )}

      {field.fieldType === 'textarea' && (
        <div
          className={cn(
            'w-full h-20 px-3 py-2 rounded-md border border-border bg-bg text-sm text-text-secondary',
            'cursor-not-allowed opacity-60',
          )}
          aria-label={`${field.displayName} textarea`}
        />
      )}

      {field.fieldType === 'date' && (
        <div className={cn(baseInput, 'flex items-center justify-between px-3')}>
          <span className="text-text-secondary text-sm opacity-60">MM / DD / YYYY</span>
        </div>
      )}

      {field.fieldType === 'checkbox' && (
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border border-border bg-bg opacity-60 flex-shrink-0" />
          <span className="text-sm text-text-secondary opacity-60">Check to confirm</span>
        </div>
      )}

      {field.fieldType === 'picker' && (
        <div className={cn(baseInput, 'flex items-center justify-between px-3')}>
          <span className="text-text-secondary text-sm opacity-60">
            {field.options.length > 0 ? `${String(field.options.length)} options` : 'Select...'}
          </span>
          <ChevronDown size={14} className="text-text-secondary opacity-60" />
        </div>
      )}

      {field.fieldType === 'photo' && (
        <div
          className={cn(
            'w-full h-20 rounded-md border-2 border-dashed border-border bg-bg',
            'flex flex-col items-center justify-center gap-1 opacity-60 cursor-not-allowed',
          )}
        >
          <span className="text-xs text-text-secondary">Tap to capture photo</span>
        </div>
      )}

      {field.fieldType === 'signature' && (
        <div
          className={cn(
            'w-full h-20 rounded-md border-2 border-dashed border-border bg-bg',
            'flex flex-col items-center justify-center gap-1 opacity-60 cursor-not-allowed',
          )}
        >
          <span className="text-xs text-text-secondary">Tap to sign</span>
        </div>
      )}
    </div>
  );
}

/* ── Field form (add/edit inline) ── */

interface FieldFormState {
  displayName: string;
  fieldType: FieldType;
  required: boolean;
  group: string;
  optionsRaw: string;
  showWhen: string;
}

const DEFAULT_FIELD_FORM: FieldFormState = {
  displayName: '',
  fieldType: 'text',
  required: false,
  group: '',
  optionsRaw: '',
  showWhen: '',
};

interface FieldFormProps {
  initial: FieldFormState;
  onSave: (state: FieldFormState) => void;
  onCancel: () => void;
  title: string;
}

function FieldForm({ initial, onSave, onCancel, title }: FieldFormProps): ReactElement {
  const [form, setForm] = useState<FieldFormState>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof FieldFormState, string>>>({});

  function validate(): boolean {
    const next: Partial<Record<keyof FieldFormState, string>> = {};
    if (form.displayName.trim() === '') {
      next.displayName = 'Display name is required';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSave(): void {
    if (validate()) {
      onSave(form);
    }
  }

  function handleNameChange(e: React.ChangeEvent<HTMLInputElement>): void {
    setForm((prev) => ({ ...prev, displayName: e.target.value }));
  }

  function handleGroupChange(e: React.ChangeEvent<HTMLInputElement>): void {
    setForm((prev) => ({ ...prev, group: e.target.value }));
  }

  function handleOptionsChange(e: React.ChangeEvent<HTMLInputElement>): void {
    setForm((prev) => ({ ...prev, optionsRaw: e.target.value }));
  }

  function handleTypeChange(e: React.ChangeEvent<HTMLSelectElement>): void {
    const val = e.target.value;
    if (isValidFieldType(val)) {
      setForm((prev) => ({ ...prev, fieldType: val }));
    }
  }

  function handleRequiredChange(e: React.ChangeEvent<HTMLInputElement>): void {
    setForm((prev) => ({ ...prev, required: e.target.checked }));
  }

  function handleShowWhenChange(e: React.ChangeEvent<HTMLInputElement>): void {
    setForm((prev) => ({ ...prev, showWhen: e.target.value }));
  }

  return (
    <div className="bg-bg-elevated border border-border rounded-lg p-4 space-y-3">
      <p className="text-sm font-semibold text-text">{title}</p>

      <div className="grid grid-cols-1 gap-3">
        <GlassInput
          label="Display Name"
          value={form.displayName}
          onChange={handleNameChange}
          error={errors.displayName ?? ''}
          placeholder="e.g. Unit Condition"
        />

        <GlassSelect
          label="Field Type"
          value={form.fieldType}
          onChange={handleTypeChange}
          options={FIELD_TYPE_OPTIONS}
        />

        <GlassInput
          label="Group"
          value={form.group}
          onChange={handleGroupChange}
          placeholder="e.g. General, Systems"
        />

        {form.fieldType === 'picker' && (
          <GlassInput
            label="Options (comma-separated)"
            value={form.optionsRaw}
            onChange={handleOptionsChange}
            placeholder="e.g. Excellent, Good, Fair, Poor"
          />
        )}

        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={form.required}
            onChange={handleRequiredChange}
            className="w-4 h-4 rounded border-border accent-primary"
          />
          <span className="text-sm text-text-secondary">Required field</span>
        </label>

        <GlassInput
          label="Show When (conditional)"
          value={form.showWhen}
          onChange={handleShowWhenChange}
          placeholder="e.g. fieldId:value or fieldId:checkbox:1"
        />
        <p className="text-xs text-text-tertiary -mt-2">
          Format: <code>fieldId:value</code> — field shows when another field equals value
        </p>
      </div>

      <div className="flex items-center gap-2 pt-1">
        <GlassButton variant="primary" size="sm" onClick={handleSave}>
          <Save size={12} />
          Save Field
        </GlassButton>
        <GlassButton variant="ghost" size="sm" onClick={onCancel}>
          <X size={12} />
          Cancel
        </GlassButton>
      </div>
    </div>
  );
}

/* ── Field row ── */

interface FieldRowProps {
  field: LocalField;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

function FieldRow({ field, onEdit, onDelete }: FieldRowProps): ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  });

  const badge = FIELD_TYPE_BADGE[field.fieldType];

  const style = {
    transform: transform !== null ? CSS.Transform.toString(transform) : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 px-4 py-3 rounded-lg border border-border bg-bg-elevated',
        'hover:border-border-strong transition-colors duration-[var(--dur)]',
        isDragging && 'shadow-lg z-10',
      )}
    >
      {/* Drag handle */}
      <button
        type="button"
        className="p-1 rounded text-text-tertiary hover:text-text hover:bg-bg-hover cursor-grab active:cursor-grabbing transition-colors flex-shrink-0"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={16} />
      </button>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-text truncate">{field.displayName}</span>
          {field.required && (
            <span className="text-xs text-danger font-medium flex-shrink-0">Required</span>
          )}
          {field.showWhen !== '' && (
            <span className="text-xs text-warning-fg font-medium flex-shrink-0">Conditional</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <GlassBadge variant={badge.variant}>{badge.label}</GlassBadge>
          {field.group !== '' && <span className="text-xs text-text-tertiary">{field.group}</span>}
          {field.fieldType === 'picker' && field.options.length > 0 && (
            <span className="text-xs text-text-tertiary">
              {String(field.options.length)} options
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <GlassButton
          variant="ghost"
          size="sm"
          onClick={() => {
            onEdit(field.id);
          }}
          aria-label={`Edit ${field.displayName}`}
        >
          <Edit2 size={12} />
        </GlassButton>
        <GlassButton
          variant="ghost"
          size="sm"
          onClick={() => {
            onDelete(field.id);
          }}
          className="text-danger hover:bg-danger-soft hover:text-danger-fg"
          aria-label={`Delete ${field.displayName}`}
        >
          <Trash2 size={12} />
        </GlassButton>
      </div>
    </div>
  );
}

/* ── Page ── */

export function ChecklistTemplateBuilderPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === undefined;
  const templateId = isNew ? 0 : parseInt(id, 10);

  // Fetch template when editing
  const { data, loading: queryLoading } = useQuery<TemplateData>(GET_CHECKLIST_TEMPLATE, {
    variables: { id: templateId },
    skip: isNew,
  });

  const [createTemplate, { loading: creatingTemplate }] = useMutation<CreateChecklistTemplateData>(
    CREATE_CHECKLIST_TEMPLATE,
    {
      refetchQueries: [{ query: GET_CHECKLIST_TEMPLATES, variables: { first: 200 } }],
    },
  );

  const [updateTemplate, { loading: updatingTemplate }] = useMutation(UPDATE_CHECKLIST_TEMPLATE, {
    refetchQueries: [{ query: GET_CHECKLIST_TEMPLATES, variables: { first: 200 } }],
  });

  const [createField, { loading: creatingField }] = useMutation<CreateChecklistTemplateFieldData>(
    CREATE_CHECKLIST_TEMPLATE_FIELD,
  );

  const [updateField, { loading: updatingField }] = useMutation(UPDATE_CHECKLIST_TEMPLATE_FIELD);

  const [deleteField] = useMutation(DELETE_CHECKLIST_TEMPLATE_FIELD);

  const isSaving = creatingTemplate || updatingTemplate || creatingField || updatingField;

  const [template, setTemplate] = useState<LocalTemplate>(EMPTY_TEMPLATE);
  const [initialized, setInitialized] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Map API template data into local state
  useEffect(() => {
    if (initialized || isNew || data === undefined) {
      return;
    }
    const apiTemplate = data.checklistTemplate;
    if (apiTemplate === null) {
      return;
    }

    const fields: LocalField[] = apiTemplate.fields
      .slice()
      .sort((a: TemplateFieldNode, b: TemplateFieldNode) => a.seq - b.seq)
      .map((f: TemplateFieldNode) => ({
        id: String(f.id),
        apiId: f.id,
        displayName: f.fieldName,
        fieldType: normalizeFieldType(f.fieldType),
        required: (f.required ?? 0) !== 0,
        group: f.group ?? '',
        options:
          f.options !== null && f.options !== ''
            ? f.options
                .split(',')
                .map((s) => s.trim())
                .filter((s) => s !== '')
            : [],
        seq: f.seq,
        showWhen: f.showWhen ?? '',
      }));

    setTemplate({
      id: apiTemplate.id,
      name: apiTemplate.name,
      description: apiTemplate.description ?? '',
      jobType: apiTemplate.jobType ?? '',
      fields,
    });
    setInitialized(true);
  }, [data, isNew, initialized]);

  // 'idle' | field-id-being-edited
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  function handleTemplateName(e: React.ChangeEvent<HTMLInputElement>): void {
    setTemplate((prev) => ({ ...prev, name: e.target.value }));
  }

  function handleTemplateDescription(e: React.ChangeEvent<HTMLInputElement>): void {
    setTemplate((prev) => ({ ...prev, description: e.target.value }));
  }

  function handleTemplateJobType(e: React.ChangeEvent<HTMLSelectElement>): void {
    setTemplate((prev) => ({ ...prev, jobType: e.target.value }));
  }

  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    if (over === null || active.id === over.id) {
      return;
    }
    setTemplate((prev) => {
      const oldIndex = prev.fields.findIndex((f) => f.id === active.id);
      const newIndex = prev.fields.findIndex((f) => f.id === over.id);
      if (oldIndex === -1 || newIndex === -1) {
        return prev;
      }
      return { ...prev, fields: arrayMove(prev.fields, oldIndex, newIndex) };
    });
  }

  function deleteLocalField(fieldId: string): void {
    const field = template.fields.find((f) => f.id === fieldId);
    if (field?.apiId !== undefined) {
      void deleteField({ variables: { id: field.apiId } });
    }
    setTemplate((prev) => ({
      ...prev,
      fields: prev.fields.filter((f) => f.id !== fieldId),
    }));
    if (editingId === fieldId) {
      setEditingId(null);
    }
  }

  function formStateToLocalField(
    state: FieldFormState,
    existingId?: string,
    existingApiId?: number,
    seq?: number,
  ): LocalField {
    const options =
      state.fieldType === 'picker'
        ? state.optionsRaw
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s !== '')
        : [];
    return {
      id: existingId ?? generateId(),
      apiId: existingApiId,
      displayName: state.displayName.trim(),
      fieldType: state.fieldType,
      required: state.required,
      group: state.group.trim(),
      options,
      seq: seq ?? template.fields.length + 1,
      showWhen: state.showWhen.trim(),
    };
  }

  function fieldToFormState(field: LocalField): FieldFormState {
    return {
      displayName: field.displayName,
      fieldType: field.fieldType,
      required: field.required,
      group: field.group,
      optionsRaw: field.options.join(', '),
      showWhen: field.showWhen,
    };
  }

  function handleAddSave(state: FieldFormState): void {
    const seq = template.fields.length + 1;
    const newField = formStateToLocalField(state, undefined, undefined, seq);
    setTemplate((prev) => ({ ...prev, fields: [...prev.fields, newField] }));
    setShowAddForm(false);
  }

  function handleEditSave(state: FieldFormState): void {
    if (editingId === null) {
      return;
    }
    const existing = template.fields.find((f) => f.id === editingId);
    const updated = formStateToLocalField(state, editingId, existing?.apiId, existing?.seq);
    setTemplate((prev) => ({
      ...prev,
      fields: prev.fields.map((f) => (f.id === editingId ? updated : f)),
    }));
    setEditingId(null);
  }

  async function handleSave(): Promise<void> {
    let resolvedTemplateId = template.id;

    if (isNew || resolvedTemplateId === undefined) {
      // Create the template first
      const result = await createTemplate({
        variables: {
          name: template.name,
          description: template.description || undefined,
          jobType: template.jobType || undefined,
        },
      });
      const newId = result.data?.createChecklistTemplate?.id;
      if (newId === undefined) {
        return;
      }
      resolvedTemplateId = newId;
      setTemplate((prev) => ({ ...prev, id: newId }));

      // Create all fields for the new template
      for (const [idx, field] of template.fields.entries()) {
        const fieldResult = await createField({
          variables: {
            templateId: resolvedTemplateId,
            seq: idx + 1,
            fieldName: field.displayName,
            fieldType: field.fieldType,
            required: field.required ? 1 : 0,
            group: field.group || undefined,
            options: field.options.length > 0 ? field.options.join(',') : undefined,
            showWhen: field.showWhen || undefined,
          },
        });
        const returnedId = fieldResult.data?.createChecklistTemplateField?.id;
        if (returnedId !== undefined) {
          const localId = field.id;
          setTemplate((prev) => ({
            ...prev,
            fields: prev.fields.map((f) => (f.id === localId ? { ...f, apiId: returnedId } : f)),
          }));
        }
      }

      void navigate(`/checklist-templates/${String(newId)}/edit`);
      return;
    }

    // Update existing template metadata
    void updateTemplate({
      variables: {
        id: resolvedTemplateId,
        name: template.name,
        description: template.description || undefined,
        jobType: template.jobType || undefined,
      },
    });

    // Sync fields: create new, update existing, seq update for all
    for (const [idx, field] of template.fields.entries()) {
      if (field.apiId === undefined) {
        // New field — create it
        const fieldResult = await createField({
          variables: {
            templateId: resolvedTemplateId,
            seq: idx + 1,
            fieldName: field.displayName,
            fieldType: field.fieldType,
            required: field.required ? 1 : 0,
            group: field.group || undefined,
            options: field.options.length > 0 ? field.options.join(',') : undefined,
            showWhen: field.showWhen || undefined,
          },
        });
        const returnedId = fieldResult.data?.createChecklistTemplateField?.id;
        if (returnedId !== undefined) {
          const localId = field.id;
          setTemplate((prev) => ({
            ...prev,
            fields: prev.fields.map((f) => (f.id === localId ? { ...f, apiId: returnedId } : f)),
          }));
        }
      } else {
        // Existing field — update it
        void updateField({
          variables: {
            id: field.apiId,
            seq: idx + 1,
            fieldName: field.displayName,
            fieldType: field.fieldType,
            required: field.required ? 1 : 0,
            group: field.group || undefined,
            options: field.options.length > 0 ? field.options.join(',') : undefined,
            showWhen: field.showWhen || undefined,
          },
        });
      }
    }
  }

  // Group fields for preview
  const groupedFields: { group: string; fields: LocalField[] }[] = [];
  for (const field of template.fields) {
    const groupName = field.group !== '' ? field.group : 'General';
    const existing = groupedFields.find((g) => g.group === groupName);
    if (existing !== undefined) {
      existing.fields.push(field);
    } else {
      groupedFields.push({ group: groupName, fields: [field] });
    }
  }

  if (queryLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-text-secondary">
        Loading…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link
            to="/checklist-templates"
            className="flex items-center justify-center w-8 h-8 rounded-md border border-border bg-bg-elevated text-text-secondary hover:text-text hover:bg-bg-hover transition-colors"
            aria-label="Back to checklist templates"
          >
            <ArrowLeft size={16} />
          </Link>
          <div>
            <h1 className="text-xl font-semibold text-text">
              {isNew ? 'New Checklist Template' : 'Edit Checklist Template'}
            </h1>
            <p className="text-sm text-text-secondary mt-0.5">
              {isNew
                ? 'Define fields for technicians to complete in the field'
                : `Editing: ${template.name}`}
            </p>
          </div>
        </div>
        <GlassButton
          variant="primary"
          size="md"
          onClick={() => {
            void handleSave();
          }}
          disabled={isSaving}
          className="self-start sm:self-auto"
        >
          <Save size={15} />
          {isSaving ? 'Saving…' : 'Save Template'}
        </GlassButton>
      </div>

      {/* Template metadata */}
      <GlassCard>
        <GlassCardHeader>
          <p className="text-sm font-semibold text-text">Template Details</p>
        </GlassCardHeader>
        <GlassCardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <GlassInput
            label="Template Name"
            value={template.name}
            onChange={handleTemplateName}
            placeholder="e.g. Standard Apartment Inspection"
          />
          <GlassInput
            label="Description"
            value={template.description}
            onChange={handleTemplateDescription}
            placeholder="Brief description of this checklist"
          />
          <GlassSelect
            label="Job Type"
            value={template.jobType}
            onChange={handleTemplateJobType}
            options={JOB_TYPE_OPTIONS}
          />
        </GlassCardContent>
      </GlassCard>

      {/* Two-panel layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        {/* Left panel: field list */}
        <GlassCard>
          <GlassCardHeader className="flex flex-row items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-text">Fields</p>
              <p className="text-xs text-text-secondary mt-0.5">
                {String(template.fields.length)} {template.fields.length === 1 ? 'field' : 'fields'}{' '}
                defined
              </p>
            </div>
            {!showAddForm && editingId === null && (
              <GlassButton
                variant="secondary"
                size="sm"
                onClick={() => {
                  setShowAddForm(true);
                }}
              >
                <Plus size={13} />
                Add Field
              </GlassButton>
            )}
          </GlassCardHeader>
          <GlassCardContent className="space-y-2">
            {/* Add field form */}
            {showAddForm && (
              <FieldForm
                title="Add New Field"
                initial={DEFAULT_FIELD_FORM}
                onSave={handleAddSave}
                onCancel={() => {
                  setShowAddForm(false);
                }}
              />
            )}

            {/* Empty state */}
            {template.fields.length === 0 && !showAddForm && (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-bg-subtle mb-3">
                  <Pencil size={18} className="text-text-tertiary" />
                </div>
                <p className="text-sm font-medium text-text">No fields yet</p>
                <p className="text-sm text-text-secondary mt-1">
                  Click "Add Field" to start building this template.
                </p>
              </div>
            )}

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={template.fields.map((f) => f.id)}
                strategy={verticalListSortingStrategy}
              >
                {template.fields.map((field) => (
                  <div key={field.id}>
                    {editingId === field.id ? (
                      <FieldForm
                        title={`Editing: ${field.displayName}`}
                        initial={fieldToFormState(field)}
                        onSave={handleEditSave}
                        onCancel={() => {
                          setEditingId(null);
                        }}
                      />
                    ) : (
                      <FieldRow
                        field={field}
                        onEdit={(fid) => {
                          setEditingId(fid);
                          setShowAddForm(false);
                        }}
                        onDelete={deleteLocalField}
                      />
                    )}
                  </div>
                ))}
              </SortableContext>
            </DndContext>
          </GlassCardContent>
        </GlassCard>

        {/* Right panel: live preview */}
        <div className="lg:sticky lg:top-6 space-y-3">
          <GlassCard>
            <GlassCardHeader>
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-text">Live Preview</p>
                <GlassBadge variant="info">Mobile View</GlassBadge>
              </div>
            </GlassCardHeader>

            {/* Phone shell */}
            <GlassCardContent className="flex justify-center py-5">
              <div
                className={cn(
                  'w-72 rounded-2xl border-2 border-border bg-bg overflow-hidden shadow-lg',
                  'flex flex-col',
                )}
              >
                {/* Fake phone status bar */}
                <div className="h-8 bg-bg-elevated border-b border-border flex items-center justify-between px-4">
                  <span className="text-xs text-text-secondary font-medium">MerriTrac</span>
                  <span className="text-xs text-text-tertiary">9:41 AM</span>
                </div>

                {/* Template header in phone */}
                <div className="px-4 py-3 bg-primary">
                  <p className="text-xs font-semibold text-primary-fg truncate">
                    {template.name !== '' ? template.name : 'Untitled Template'}
                  </p>
                  {template.description !== '' && (
                    <p className="text-xs text-primary-fg opacity-80 mt-0.5 truncate">
                      {template.description}
                    </p>
                  )}
                  {template.jobType !== '' && (
                    <p className="text-xs text-primary-fg opacity-60 mt-0.5 truncate capitalize">
                      {template.jobType.replace('_', ' ')}
                    </p>
                  )}
                </div>

                {/* Fields */}
                <div className="flex-1 overflow-y-auto max-h-96 px-3 py-3 space-y-4">
                  {template.fields.length === 0 && (
                    <p className="text-xs text-text-tertiary text-center py-8">
                      No fields to preview
                    </p>
                  )}

                  {groupedFields.map((group) => (
                    <div key={group.group} className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
                          {group.group}
                        </span>
                        <div className="flex-1 h-px bg-border-subtle" />
                      </div>
                      {group.fields.map((field) => (
                        <PreviewField key={field.id} field={field} />
                      ))}
                    </div>
                  ))}
                </div>

                {/* Submit button in phone */}
                {template.fields.length > 0 && (
                  <div className="px-3 py-3 border-t border-border">
                    <div className="w-full h-9 rounded-md bg-primary flex items-center justify-center opacity-80">
                      <span className="text-xs font-semibold text-primary-fg">
                        Submit Checklist
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </GlassCardContent>
          </GlassCard>

          {/* Summary stats */}
          {template.fields.length > 0 && (
            <GlassCard>
              <GlassCardContent className="py-3">
                <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">
                  Summary
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-lg font-semibold text-text">
                      {String(template.fields.length)}
                    </p>
                    <p className="text-xs text-text-secondary">Total fields</p>
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-text">
                      {String(template.fields.filter((f) => f.required).length)}
                    </p>
                    <p className="text-xs text-text-secondary">Required</p>
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-text">
                      {String(groupedFields.length)}
                    </p>
                    <p className="text-xs text-text-secondary">Groups</p>
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-text">
                      {String(
                        template.fields.filter(
                          (f) => f.fieldType === 'photo' || f.fieldType === 'signature',
                        ).length,
                      )}
                    </p>
                    <p className="text-xs text-text-secondary">Media fields</p>
                  </div>
                </div>
              </GlassCardContent>
            </GlassCard>
          )}
        </div>
      </div>
    </div>
  );
}
